import { EntitiesState } from '@redux/entities'

import type { OrderItemOption, ProviderPriceListItemModel } from '@models/types'

export type NewOption = {
  id: number,
  title: string,
  value: {
    id: number,
    is_default?: boolean,
    is_upgrade?: boolean,
    is_downgrade?: boolean,
    title: string,
    price: number,
    provider_product_option_value_code: string,
  },
}

type AutoSelectProductOptionsParams = {
  providerPriceListItem: ProviderPriceListItemModel,
  providerPriceListItemOptionValues: EntitiesState['providerPriceListItemOptionValues'],
  providerPriceListItemOptions: EntitiesState['providerPriceListItemOptions'],
}

const autoSelectProductOptions = (params: AutoSelectProductOptionsParams) => {
  const {
    providerPriceListItem,
    providerPriceListItemOptionValues,
    providerPriceListItemOptions,
  } = params

  const filteredPriceListItemOptions = Object.values(providerPriceListItemOptions)
    .filter((priceListItemOption) => {
      const { hidden, provider_price_list_item_id } = priceListItemOption
      return !hidden && providerPriceListItem?.id === provider_price_list_item_id
    })

  if (!filteredPriceListItemOptions.length){
    return { selectedProviderPriceListItemOptions: [] }
  }

  const options = filteredPriceListItemOptions.reduce((acc, option) => {
    const filteredPriceListItemOptionValues = Object.values(providerPriceListItemOptionValues)
      .filter((priceListItemOptionValue) => {
        const { hidden, provider_price_list_item_option_id } = priceListItemOptionValue
        return !hidden && option.id === provider_price_list_item_option_id
      })

    if (filteredPriceListItemOptionValues.length > 0){
      const sortedPriceListItemOptionValues = filteredPriceListItemOptionValues
        .sort((a, b) => {
          if (a.is_default && !b.is_default) return -1
          if (!a.is_default && b.is_default) return 1

          return a.sort - b.sort
        })

      const optionValue = sortedPriceListItemOptionValues[0]

      const newOption: NewOption = {
        id: option.id,
        title: option.title || `Option #${option.id}`,
        value: {
          id: optionValue.id,
          is_default: !!optionValue.is_default,
          is_upgrade: !!optionValue.is_upgrade,
          is_downgrade: !!optionValue.is_downgrade,
          title: optionValue.title,
          price: optionValue.price,
          // @ts-expect-error TS2322
          provider_product_option_value_code: optionValue.product_code,
        },
      }

      acc.push(newOption)
    }

    return acc
  }, [] as NewOption[])

  return { selectedProviderPriceListItemOptions: options }
}

export const autoSelectApiPricingOptions = (
  params: AutoSelectProductOptionsParams & { itemOptions: OrderItemOption[] },
) => {
  const {
    itemOptions,
    providerPriceListItem,
    providerPriceListItemOptionValues,
    providerPriceListItemOptions,
  } = params

  const filteredPriceListItemOptions = Object.values(providerPriceListItemOptions)
    .filter((priceListItemOption) => {
      const { hidden, provider_price_list_item_id } = priceListItemOption
      return !hidden && providerPriceListItem?.id === provider_price_list_item_id
    })

  const selectedProviderPriceListItemOptions = itemOptions.reduce((acc, option) => {
    const existingOption = filteredPriceListItemOptions.find(
      priceListItemOption => priceListItemOption.title === option.title,
    )

    if (!existingOption) return acc

    const filteredPriceListItemOptionValues = Object.values(providerPriceListItemOptionValues)
      .filter((priceListItemOptionValue) => {
        const { hidden, provider_price_list_item_option_id } = priceListItemOptionValue
        return !hidden && existingOption.id === provider_price_list_item_option_id
      })

    const existingOptionValue = filteredPriceListItemOptionValues.find(
      priceListItemOptionValue => priceListItemOptionValue.title === option.value.title,
    )

    if (!existingOptionValue) return acc

    const newOption: NewOption = {
      id: existingOption.id,
      title: existingOption.title || `Option #${existingOption.id}`,
      value: {
        id: existingOptionValue.id,
        is_default: !!existingOptionValue.is_default,
        is_upgrade: !!existingOptionValue.is_upgrade,
        is_downgrade: !!existingOptionValue.is_downgrade,
        title: existingOptionValue.title,
        price: existingOptionValue.price,
        // @ts-expect-error TS2322
        provider_product_option_value_code: existingOptionValue.product_code,
      },
    }

    acc.push(newOption)

    return acc
  }, [] as NewOption[])

  return { selectedProviderPriceListItemOptions }
}

export default autoSelectProductOptions
